import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { createLegalActivity } from "../../services/legalActivityService";
import { createSignRequest, updateSignRequest } from "./services/signRequestService";
import { notifyActionRequired } from "../../services/notificationService";
import type { SignRequest, SignRequestStatus, SignSourceType } from "../../types/signRequest";
import { serverTimestamp } from "../../../firebaseClient";
import { isLegal as checkIsLegal, isManager as checkIsManager, isSuperAdmin, LEGAL_EMAILS, SUPERVISOR_LISA_EMAILS, SUPERVISOR_IRENE_EMAILS } from "../../config/roles";
import { logActivity } from "../../services/activityLog";

export function useSignRequestWorkflow() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const userEmail = user?.email;
  const isManager = checkIsManager(userEmail);
  const isLegal = checkIsLegal(userEmail);
  const isSuper = isSuperAdmin(userEmail);

  const isLisa = userEmail?.toLowerCase().includes("lisa") || isSuper;
  const isIrene = userEmail?.toLowerCase().includes("irene") || isSuper;

  // 1. Create Draft
  const createDraftFromSource = async (
    sourceInfo: { sourceType: SignSourceType; sourceId: string; sourceTitle: string; sourcePath?: string; category: string },
    initialData?: Partial<SignRequest>
  ) => {
    if (!user) throw new Error("User not logged in");
    setLoading(true);
    try {
      const now = new Date().toISOString();
      const { category, ...restSourceInfo } = sourceInfo;
      
      // Fix TS2783: Destructure category from initialData to prevent duplicate key in object literal
      const { category: initCategory, ...safeInitialData } = initialData || {};
      const finalCategory = initCategory || category;

      const payload = {
        title: sourceInfo.sourceTitle,
        description: "",
        attachments: [],
        ...safeInitialData,
        ...restSourceInfo,
        category: finalCategory,
        status: "DRAFT" as SignRequestStatus,
        createdAt: serverTimestamp(),
        createdByUserId: user.id,
        createdByUserName: user.displayName,
        createdByDeptId: "",
        createdByDeptName: "User Dept",
        history: [{
          at: now,
          actorId: user.id,
          actorName: user.displayName,
          action: "CREATED",
          note: "建立簽呈草稿"
        }]
      };
      const id = await createSignRequest(payload as any);
      
      await createLegalActivity({
        sourceType: sourceInfo.sourceType,
        sourceId: sourceInfo.sourceId,
        title: `[簽呈草稿] ${sourceInfo.sourceTitle}`,
        actorUserId: user.id,
        actorUserName: user.displayName,
        logOnly: true,
        actionLabel: "SIGN_DRAFT"
      });

      return id;
    } finally {
      setLoading(false);
    }
  };

  // 2. Submit to Legal
  const submitToLegal = async (req: SignRequest) => {
    if (!user) return;
    setLoading(true);
    try {
      const historyItem = {
        at: new Date().toISOString(),
        actorId: user.id,
        actorName: user.displayName,
        action: "SUBMITTED_TO_LEGAL",
        note: "送交法務審查"
      };
      
      await updateSignRequest(req.id, {
        status: "PENDING_LEGAL",
        history: [...req.history, historyItem] as any
      });

      await createLegalActivity({
        sourceType: `signRequest:${req.sourceType}`,
        sourceId: req.id,
        title: `[簽呈送審] ${req.title}`,
        actorUserId: user.id,
        actorUserName: user.displayName,
        actionLabel: "簽呈送審",
        todoRequired: true,
        assigneeName: "Legal Team",
        assigneeId: "group_legal",
        description: `來源：${req.sourceType}\n說明：${req.description}`
      });

      // System Log
      await logActivity({
        module: "seal",
        action: "submit",
        entityId: req.id,
        entityType: "SignRequest",
        actorId: user.id,
        actorName: user.displayName,
        details: `Submitted sign request: ${req.title}`
      });

      // Email Notification to Legal
      await notifyActionRequired({
          docType: "contractReview", // close enough mapping
          docId: req.id,
          docTitle: req.title,
          assigneeId: "group_legal",
          assigneeEmail: LEGAL_EMAILS[0] || 'kevin@sunfriend.tw',
          actionType: "REVIEW",
          note: `申請人：${user.displayName}`
      });

    } finally {
      setLoading(false);
    }
  };

  // 3. Legal Approve (Transition to Manager)
  const legalApprove = async (req: SignRequest, opinion: string) => {
    if (!user) return;
    setLoading(true);
    try {
      const historyItem = {
        at: new Date().toISOString(),
        actorId: user.id,
        actorName: user.displayName,
        action: "LEGAL_APPROVED",
        note: opinion
      };

      await updateSignRequest(req.id, {
        status: "PENDING_MANAGER",
        legalReviewerId: user.id,
        legalReviewerName: user.displayName,
        legalReviewAt: new Date().toISOString(),
        legalOpinion: opinion,
        history: [...req.history, historyItem] as any
      });

      await createLegalActivity({
        sourceType: `signRequest:${req.sourceType}`,
        sourceId: req.id,
        title: `[簽呈] 法務審查通過`,
        actorUserId: user.id,
        actorUserName: user.displayName,
        actionLabel: "簽呈送主管",
        todoRequired: true,
        assigneeName: "Irene/Lisa",
        assigneeId: "executive_team",
        priority: "High",
        description: `法務意見：${opinion}\n待主管批示。`
      });

      // System Log
      await logActivity({
        module: "seal",
        action: "approve",
        entityId: req.id,
        entityType: "SignRequest",
        actorId: user.id,
        actorName: user.displayName,
        details: `Legal approved: ${opinion}`
      });

      // Email Notification to Supervisors
      for (const email of [...SUPERVISOR_LISA_EMAILS, ...SUPERVISOR_IRENE_EMAILS]) {
          await notifyActionRequired({
              docType: "contractReview",
              docId: req.id,
              docTitle: req.title,
              assigneeId: "supervisor",
              assigneeEmail: email,
              actionType: "APPROVE",
              note: `法務意見：${opinion}`
          });
      }

    } finally {
      setLoading(false);
    }
  };

  // 4. Legal Return
  const legalReturn = async (req: SignRequest, opinion: string, reason: string) => {
    if (!user) return;
    setLoading(true);
    try {
      const historyItem = {
        at: new Date().toISOString(),
        actorId: user.id,
        actorName: user.displayName,
        action: "LEGAL_RETURNED",
        note: `退回理由：${reason}\n意見：${opinion}`
      };

      await updateSignRequest(req.id, {
        status: "LEGAL_RETURNED",
        legalReviewerId: user.id,
        legalReviewerName: user.displayName,
        legalReviewAt: new Date().toISOString(),
        legalOpinion: opinion,
        legalReturnReason: reason,
        history: [...req.history, historyItem] as any
      });

      await createLegalActivity({
        sourceType: `signRequest:${req.sourceType}`,
        sourceId: req.id,
        title: `[簽呈] 法務退回`,
        actorUserId: user.id,
        actorUserName: user.displayName,
        actionLabel: "簽呈退回",
        todoRequired: true,
        assigneeName: req.createdByUserName,
        assigneeId: req.createdByUserId,
        description: `退回理由：${reason}`
      });

      // System Log
      await logActivity({
        module: "seal",
        action: "reject",
        entityId: req.id,
        entityType: "SignRequest",
        actorId: user.id,
        actorName: user.displayName,
        details: `Legal returned: ${reason}`
      });

    } finally {
      setLoading(false);
    }
  };

  // 5. Manager Action (Dual Approval Logic)
  const managerAction = async (
    req: SignRequest, 
    decision: "APPROVED" | "REJECTED", 
    comment: string,
    managerKey: "Lisa" | "Irene"
  ) => {
    if (!user) return;
    setLoading(true);
    try {
      const historyItem = {
        at: new Date().toISOString(),
        actorId: user.id,
        actorName: user.displayName,
        action: decision === "APPROVED" ? "MANAGER_APPROVED" : "MANAGER_REJECTED",
        note: `[${managerKey}] ${comment}`
      };

      const patch: Partial<SignRequest> = {};
      const nowIso = new Date().toISOString();

      if (managerKey === "Lisa") {
        patch.managerLisaComment = comment;
        patch.managerLisaDecision = decision;
        patch.managerLisaDecisionAt = nowIso;
      } else {
        patch.managerIreneComment = comment;
        patch.managerIreneDecision = decision;
        patch.managerIreneDecisionAt = nowIso;
      }

      let nextStatus: SignRequestStatus = "PENDING_MANAGER";

      if (decision === "REJECTED") {
        nextStatus = "REJECTED";
      } else {
        const otherApproved = managerKey === "Lisa" 
            ? req.managerIreneDecision === "APPROVED" 
            : req.managerLisaDecision === "APPROVED";
        
        if (otherApproved) {
            nextStatus = "APPROVED";
        }
      }

      patch.status = nextStatus;
      patch.managerId = user.id;
      patch.managerName = user.displayName;
      patch.managerDecision = decision; 
      patch.managerDecisionAt = nowIso;
      patch.history = [...req.history, historyItem] as any;

      await updateSignRequest(req.id, patch);

      const actionLabel = nextStatus === "APPROVED" ? "簽呈核准(最終)" : nextStatus === "REJECTED" ? "簽呈駁回" : `簽呈主管核准(${managerKey})`;
      
      await createLegalActivity({
        sourceType: `signRequest:${req.sourceType}`,
        sourceId: req.id,
        title: `[${actionLabel}] ${req.title}`,
        actorUserId: user.id,
        actorUserName: user.displayName,
        actionLabel: actionLabel,
        logOnly: nextStatus !== "REJECTED" && nextStatus !== "APPROVED",
        todoRequired: nextStatus === "APPROVED" || nextStatus === "REJECTED",
        assigneeName: req.createdByUserName,
        assigneeId: req.createdByUserId,
        description: `主管(${managerKey})批示：${comment}\n狀態：${nextStatus}`
      });

      // System Log
      await logActivity({
        module: "seal",
        action: decision === "APPROVED" ? "approve" : "reject",
        entityId: req.id,
        entityType: "SignRequest",
        actorId: user.id,
        actorName: user.displayName,
        details: `Manager(${managerKey}) ${decision}: ${comment}`
      });
      
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    isManager,
    isLegal,
    isLisa,
    isIrene,
    createDraftFromSource,
    submitToLegal,
    legalApprove,
    legalReturn,
    managerAction
  };
}
